import DOMPurify from 'isomorphic-dompurify';
import type { Config } from 'isomorphic-dompurify';

import { PURIFY_CONFIG } from '../ai/embed/purify';
import type { IzQuestion } from '../../question-bank/izteach';
import { imageSource } from './fromExam';

// IZ exam math is <img> MathJax SVGs, so exam html keeps img[src] on top of the shared config
const EXAM_PURIFY: Config = {
  ...PURIFY_CONFIG,
  ALLOWED_TAGS: (PURIFY_CONFIG.ALLOWED_TAGS || []).concat(['img']),
  ALLOWED_ATTR: (PURIFY_CONFIG.ALLOWED_ATTR || []).concat(['src', 'alt']),
};

/** Sanitized exam html with every image on a live, https host; an unusable image is dropped. */
export function sanitizeExamHtml(html: string): string {
  if (!html) return '';
  const clean = DOMPurify.sanitize(html, EXAM_PURIFY) as string;
  return clean.replace(/<img\b[^>]*>/gi, (tag) => {
    const match = /\bsrc="([^"]*)"/i.exec(tag);
    const url = match ? imageSource(match[1]) : null;
    if (!url) return '';
    // MathJax SVGs sit in the text line; anything else is a figure on its own row
    const kind = /\.svg(\?|$)/i.test(url) || /^data:image\/svg/i.test(url) ? 'exam-formula' : 'exam-figure';
    return '<img class="' + kind + '" src="' + url.replace(/"/g, '&quot;') + '" alt="">';
  });
}

function sanitizeAnswer(answer: unknown): unknown {
  if (!answer || typeof answer !== 'object') return answer;
  const record = answer as { value?: unknown };
  return { ...record, value: sanitizeExamHtml(String(record.value || '')) };
}

/** A row with its stem, answers and text key sanitized; the canvas never parses html, this is defence in depth. */
export function sanitizeExamRow(raw: IzQuestion): IzQuestion {
  const next: IzQuestion = { ...raw, html: sanitizeExamHtml(typeof raw.html === 'string' ? raw.html : '') };
  if (typeof raw.correct === 'string') next.correct = sanitizeExamHtml(raw.correct);
  const answers = raw.answers;
  if (Array.isArray(answers)) next.answers = answers.map(sanitizeAnswer) as IzQuestion['answers'];
  else if (answers && typeof answers === 'object') {
    const map: Record<string, unknown> = {};
    Object.keys(answers).forEach((key) => { map[key] = sanitizeAnswer(answers[key]); });
    next.answers = map as IzQuestion['answers'];
  }
  return next;
}
